/**
 * Générateur — Calculer une proportion et l'exprimer sous forme de pourcentage
 * Attendu ID : 635 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 3 EXERCICES DIDACTIQUEMENT DISTINCTS, déclinés en
 * plusieurs formats UI pour un total de 6 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER19 §6.1 à §6.6) :
 *
 *   1. `proportion_vers_pourcentage`  → QCM, Saisie, Texte à trous.
 *   2. `comparer_proportions`         → QCM, Texte à trous (saisie).
 *   3. `complement_pourcentage`       → QCM.
 *
 * Principes clés :
 *   - Proportion = partie / tout. Les dénominateurs divisent ou multiplient 100.
 *   - Toujours convertir en /100, puis en pourcentage.
 *   - Distracteurs ciblent les erreurs courantes : inversion, oubli /100, etc.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { gcd } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 635;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PROP_QCM   = 'Calculer une proportion et l\'exprimer en pourcentage (QCM)';
const L_PROP_SAISIE = 'Calculer une proportion et l\'exprimer en pourcentage (saisie)';
const L_PROP_GUIDEE = 'Calculer une proportion en étapes (texte à trous)';
const L_COMP_QCM    = 'Comparer deux proportions';
const L_COMP_SAISIE = 'Comparer deux proportions (calcul détaillé)';
const L_COMPL_QCM   = 'Calculer le complément à 100 %';

// Liste fixe de prénoms (règle projet)
const PRENOMS = [
    'Adam', 'Amélie', 'Chloé', 'Hugo', 'Inès', 'Jade',
    'Karim', 'Léo', 'Lucas', 'Manon', 'Sofia', 'Thomas', 'Yanis',
];

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

/** Convertit une fraction (partie/tout) en pourcentage entier. */
const proportionToPercent = (partie, tout) => {
    return Math.round((partie / tout) * 100);
};

// ═══════════════════════════════════════════════════════════
// POOLS
// ═══════════════════════════════════════════════════════════

/**
 * Couples (total, partie) où total divise 100.
 * Cela garantit que partie/total × 100 est un entier.
 */
const DIVISOR_COUPLES = [
    // total=2
    { total: 2, part: 1 },        // 50%
    // total=4
    { total: 4, part: 1 },        // 25%
    { total: 4, part: 3 },        // 75%
    // total=5
    { total: 5, part: 1 },        // 20%
    { total: 5, part: 2 },        // 40%
    { total: 5, part: 3 },        // 60%
    { total: 5, part: 4 },        // 80%
    // total=10
    { total: 10, part: 1 },       // 10%
    { total: 10, part: 3 },       // 30%
    { total: 10, part: 7 },       // 70%
    { total: 10, part: 9 },       // 90%
    // total=20
    { total: 20, part: 1 },       // 5%
    { total: 20, part: 3 },       // 15%
    { total: 20, part: 7 },       // 35%
    { total: 20, part: 9 },       // 45%
    { total: 20, part: 11 },      // 55%
    { total: 20, part: 13 },      // 65%
    { total: 20, part: 16 },      // 80%
    // total=25
    { total: 25, part: 1 },       // 4%
    { total: 25, part: 2 },       // 8%
    { total: 25, part: 3 },       // 12%
    { total: 25, part: 5 },       // 20%
    { total: 25, part: 7 },       // 28%
    { total: 25, part: 9 },       // 36%
    // total=50
    { total: 50, part: 1 },       // 2%
    { total: 50, part: 3 },       // 6%
    { total: 50, part: 7 },       // 14%
    { total: 50, part: 9 },       // 18%
    { total: 50, part: 11 },      // 22%
    { total: 50, part: 15 },      // 30%
    { total: 50, part: 21 },      // 42%
];

/**
 * Couples (total, partie) où total est un multiple de 100.
 * On divise numérateur et dénominateur par total/100.
 */
const MULTIPLE_COUPLES = [
    { total: 200, part: 30 },     // 15%
    { total: 200, part: 50 },     // 25%
    { total: 200, part: 80 },     // 40%
    { total: 200, part: 120 },    // 60%
    { total: 200, part: 170 },    // 85%
    { total: 300, part: 15 },     // 5%
    { total: 300, part: 60 },     // 20%
    { total: 300, part: 90 },     // 30%
    { total: 300, part: 150 },    // 50%
    { total: 300, part: 210 },    // 70%
    { total: 400, part: 20 },     // 5%
    { total: 400, part: 80 },     // 20%
    { total: 400, part: 120 },    // 30%
    { total: 400, part: 200 },    // 50%
    { total: 400, part: 340 },    // 85%
    { total: 500, part: 25 },     // 5%
    { total: 500, part: 50 },     // 10%
    { total: 500, part: 150 },    // 30%
    { total: 500, part: 250 },    // 50%
    { total: 500, part: 400 },    // 80%
];

const ALL_COUPLES = [...DIVISOR_COUPLES, ...MULTIPLE_COUPLES];

/**
 * 8 contextes avec templates.
 * Chacun porte sur un rapport partie/total naturel et contextualisé.
 */
const CONTEXTES = [
    {
        domaine: 'classe',
        totalLabel: 'élèves',
        partLabel: 'absents',
        template: (part, total) => `Dans une classe de ${total} élèves, ${part} sont absents.`,
    },
    {
        domaine: 'club',
        totalLabel: 'membres',
        partLabel: 'qui pratiquent le tennis',
        template: (part, total) => `Dans un club de ${total} membres, ${part} pratiquent le tennis.`,
    },
    {
        domaine: 'récolte',
        totalLabel: 'pommes',
        partLabel: 'abîmées',
        template: (part, total) => `Une fermière a récolté ${total} pommes dont ${part} sont abîmées.`,
    },
    {
        domaine: 'examen',
        totalLabel: 'questions',
        partLabel: 'réussies',
        template: (part, total) => `À un examen, un élève répond bien à ${part} questions sur ${total}.`,
    },
    {
        domaine: 'bibliothèque',
        totalLabel: 'livres',
        partLabel: 'empruntés',
        template: (part, total) => `Une bibliothèque possède ${total} livres dont ${part} sont empruntés.`,
    },
    {
        domaine: 'élection',
        totalLabel: 'votants',
        partLabel: 'pour le candidat',
        template: (part, total) => `Lors d'un scrutin, ${part} votants sur ${total} ont voté pour le candidat A.`,
    },
    {
        domaine: 'billes',
        totalLabel: 'billes',
        partLabel: 'bleues',
        template: (part, total) => `Un enfant a ${total} billes dont ${part} sont bleues.`,
    },
    {
        domaine: 'trajet',
        totalLabel: 'km',
        partLabel: 'parcourus en vélo',
        template: (part, total) => `Un cycliste parcourt ${total} km et fait ${part} km en vélo.`,
    },
];

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — proportion_vers_pourcentage (QCM, Saisie, Texte à trous)
// ═══════════════════════════════════════════════════════════

/**
 * QCM : convertir une proportion en pourcentage
 */
const genProportionQcm = () => {
    const couple = randomChoice(ALL_COUPLES);
    const ctx = randomChoice(CONTEXTES);

    const { part, total } = couple;
    const percent = proportionToPercent(part, total);

    const enonce = ctx.template(part, total);
    const texte = `<p>${enonce}</p><p>Quel pourcentage cela représente-t-il\u00a0?</p>`;

    // Distracteurs contextualisés (fiche §4)
    const distractors = new Set();

    // D1 : Le numérateur brut comme pourcentage (12 sur 30 → 12 %)
    distractors.add(String(part));

    // D2 : Le dénominateur comme pourcentage (12 sur 30 → 30 %)
    distractors.add(String(total));

    // D3 : Proportion inversée / 100 (mais capped à plausible)
    const inverted = Math.round((total / part) * 100);
    if (inverted <= 200 && inverted !== percent) {
        distractors.add(String(inverted));
    }

    // D4 : Valeur proche (±10 %)
    const nearby = Math.max(1, percent + (Math.random() < 0.5 ? -10 : 10));
    if (nearby !== percent) {
        distractors.add(String(nearby));
    }

    // Ensure exactly 3 unique distractors different from correct
    const dArr = [...distractors]
        .filter(d => d !== String(percent))
        .map(x => parseInt(x, 10))
        .slice(0, 3);
    while (dArr.length < 3) {
        const fallback = randInt(1, 99);
        if (fallback !== percent && !dArr.includes(fallback)) {
            dArr.push(fallback);
        }
    }

    const choix = [String(percent), ...dArr.map(String)];
    const payload = makeQcm(choix, String(percent), texte, opts(L_PROP_QCM));
    payload._meta.generatorKey = 'proportion_vers_pourcentage';
    return payload;
};

/**
 * Saisie : convertir une proportion en pourcentage
 */
const genProportionSaisie = () => {
    const couple = randomChoice(ALL_COUPLES);
    const ctx = randomChoice(CONTEXTES);

    const { part, total } = couple;
    const percent = proportionToPercent(part, total);

    const enonce = ctx.template(part, total);
    const texte = `<p>${enonce}</p><p>Quel pourcentage cela représente-t-il\u00a0? Donne ta réponse sous la forme d'un nombre suivi du symbole %.</p>`;

    const payload = makeSaisie(String(percent), texte, {
        ...opts(L_PROP_SAISIE),
        strict: false,
        suffixe: '%',
    });
    payload._meta.generatorKey = 'proportion_vers_pourcentage';
    return payload;
};

/**
 * Texte à trous (saisie) : convertir une proportion en étapes
 * Phrase : part/total = {{numSur100}}/100 = {{percent}} %
 */
const genProportionGuidee = () => {
    const couple = randomChoice(ALL_COUPLES);
    const ctx = randomChoice(CONTEXTES);

    const { part, total } = couple;
    const percent = proportionToPercent(part, total);

    // Calculer le numérateur sur 100
    const numSur100 = Math.round((part / total) * 100);

    const enonce = ctx.template(part, total);
    const texte = `<p>${enonce}</p><p>Exprime cette proportion en pourcentage en complétant les étapes.</p>`;

    const phrase =
        `$\\dfrac{${part}}{${total}} = \\dfrac{{{${numSur100}}}}{100} = {{${percent}}}\\,\\%$`;

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_PROP_GUIDEE),
        mode: 'saisie',
        strict: false,
    });
    payload._meta.generatorKey = 'proportion_vers_pourcentage';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — comparer_proportions (QCM, Texte à trous)
// ═══════════════════════════════════════════════════════════

/**
 * QCM : comparer deux proportions
 * Important : choisir deux couples où comparaison de parties brutes
 * donne le contraire de comparaison de pourcentages.
 */
const genComparerQcm = () => {
    // Pioche deux couples avec des contextes différents
    let couple1, couple2;
    let percent1, percent2;

    // Boucle jusqu'à trouver une paire "intéressante"
    let attempts = 0;
    do {
        couple1 = randomChoice(ALL_COUPLES);
        couple2 = randomChoice(ALL_COUPLES);
        percent1 = proportionToPercent(couple1.part, couple1.total);
        percent2 = proportionToPercent(couple2.part, couple2.total);
        attempts++;
    } while (
        (couple1.total === couple2.total ||
            percent1 === percent2 ||
            (couple1.part < couple2.part) === (percent1 < percent2)) &&
        attempts < 20
    );

    // Si on n'a pas trouvé de paire "inversée", on prend juste deux couples distincts
    if (couple1 === couple2) {
        couple2 = randomChoice(ALL_COUPLES.filter((c) => c !== couple1));
        percent2 = proportionToPercent(couple2.part, couple2.total);
    }

    const [nom1, nom2] = shuffle(PRENOMS).slice(0, 2);

    const ctx1 = randomChoice(CONTEXTES);
    const ctx2 = randomChoice(CONTEXTES.filter((c) => c !== ctx1));

    const enonce1 = ctx1.template(couple1.part, couple1.total);
    const enonce2 = ctx2.template(couple2.part, couple2.total);

    const texte = `<p><strong>${nom1}</strong> : ${enonce1}</p>` +
                  `<p><strong>${nom2}</strong> : ${enonce2}</p>` +
                  `<p>Qui a le meilleur pourcentage\u00a0?</p>`;

    const correctAnswer = percent1 > percent2 ? nom1 : nom2;
    const choix = [nom1, nom2, 'Ils ont le même pourcentage', 'On ne peut pas comparer'];

    const payload = makeQcm(choix, correctAnswer, texte, opts(L_COMP_QCM));
    payload._meta.generatorKey = 'comparer_proportions';
    return payload;
};

/**
 * Texte à trous (saisie) : comparer deux proportions avec calcul détaillé
 */
const genComparerSaisie = () => {
    let couple1, couple2;
    let percent1, percent2;
    let attempts = 0;

    do {
        couple1 = randomChoice(ALL_COUPLES);
        couple2 = randomChoice(ALL_COUPLES);
        percent1 = proportionToPercent(couple1.part, couple1.total);
        percent2 = proportionToPercent(couple2.part, couple2.total);
        attempts++;
    } while ((couple1.total === couple2.total || percent1 === percent2) && attempts < 20);

    if (couple1 === couple2) {
        couple2 = randomChoice(ALL_COUPLES.filter((c) => c !== couple1));
        percent2 = proportionToPercent(couple2.part, couple2.total);
    }

    const [nom1, nom2] = shuffle(PRENOMS).slice(0, 2);
    const ctx1 = randomChoice(CONTEXTES);
    const ctx2 = randomChoice(CONTEXTES.filter((c) => c !== ctx1));

    const enonce1 = ctx1.template(couple1.part, couple1.total);
    const enonce2 = ctx2.template(couple2.part, couple2.total);

    const texte = `<p><strong>${nom1}</strong> : ${enonce1}</p>` +
                  `<p><strong>${nom2}</strong> : ${enonce2}</p>` +
                  `<p>Calcule les deux pourcentages et indique qui a le meilleur résultat.</p>`;

    // Phrase avec 2 trous
    const phrase =
        `${nom1} a {{${percent1}}} % et ${nom2} a {{${percent2}}} %. ` +
        `${percent1 > percent2 ? nom1 : nom2} a le meilleur pourcentage.`;

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_COMP_SAISIE),
        mode: 'saisie',
        strict: false,
    });
    payload._meta.generatorKey = 'comparer_proportions';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — complement_pourcentage (QCM)
// ═══════════════════════════════════════════════════════════

/**
 * QCM : calculer le complément à 100 %
 * "Si a % des élèves sont des filles, quel pourcentage sont des garçons ?"
 */
const genComplementQcm = () => {
    const couple = randomChoice(ALL_COUPLES);
    const ctx = randomChoice(CONTEXTES);

    const { part, total } = couple;
    const percent = proportionToPercent(part, total);
    const complement = 100 - percent;

    // Construire un énoncé avec deux catégories
    const enonce = ctx.template(part, total);
    const partLabel = ctx.partLabel;
    const negLabel = ctx.domaine === 'classe' ? 'présents' :
                     ctx.domaine === 'club' ? 'qui ne pratiquent pas le tennis' :
                     ctx.domaine === 'récolte' ? 'en bon état' :
                     ctx.domaine === 'examen' ? 'ratées' :
                     ctx.domaine === 'bibliothèque' ? 'disponibles' :
                     ctx.domaine === 'élection' ? 'pour d\'autres candidats' :
                     ctx.domaine === 'billes' ? 'non bleues' :
                     'parcourus en d\'autres moyens';

    const texte = `<p>${enonce}</p>` +
                  `<p>Quel pourcentage sont ${negLabel}\u00a0?</p>`;

    // Distracteurs
    const distractors = new Set();

    // D1 : Oubli du complément (redonne le même %)
    distractors.add(String(percent));

    // D2 : 100 + a au lieu de 100 - a
    distractors.add(String(100 + percent));

    // D3 : a / 2
    const half = Math.round(percent / 2);
    if (half !== complement && half !== percent) {
        distractors.add(String(half));
    }

    // D4 : Valeur proche
    const nearby = Math.max(1, complement + (Math.random() < 0.5 ? -5 : 5));
    if (nearby !== complement && nearby !== percent) {
        distractors.add(String(nearby));
    }

    // Ensure exactly 3 unique distractors
    const dArr = [...distractors]
        .filter(d => d !== String(complement))
        .map(x => parseInt(x, 10))
        .slice(0, 3);
    while (dArr.length < 3) {
        const fallback = randInt(1, 99);
        if (fallback !== complement && !dArr.includes(fallback)) {
            dArr.push(fallback);
        }
    }

    const choix = [String(complement), ...dArr.map(String)];
    const payload = makeQcm(choix, String(complement), texte, opts(L_COMPL_QCM));
    payload._meta.generatorKey = 'complement_pourcentage';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICES PUBLIQUES & REGISTRY
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    proportion_vers_pourcentage_qcm:         genProportionQcm,
    proportion_vers_pourcentage_saisie:      genProportionSaisie,
    proportion_vers_pourcentage_texte_trou:  genProportionGuidee,
    comparer_proportions_qcm:               genComparerQcm,
    comparer_proportions_saisie:            genComparerSaisie,
    complement_pourcentage_qcm:             genComplementQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    {
        id: 'proportion_vers_pourcentage',
        label: 'Calculer une proportion en pourcentage',
        formatsList: buildFormats('proportion_vers_pourcentage'),
    },
    {
        id: 'comparer_proportions',
        label: 'Comparer deux proportions',
        formatsList: buildFormats('comparer_proportions'),
    },
    {
        id: 'complement_pourcentage',
        label: 'Complément à 100 %',
        formatsList: buildFormats('complement_pourcentage'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    ALL_COUPLES, CONTEXTES, PRENOMS,
    proportionToPercent, gcd,
    genProportionQcm, genProportionSaisie, genProportionGuidee,
    genComparerQcm, genComparerSaisie, genComplementQcm,
};
